// Custom DS-Table column for Labels: the project's labels as the same WorkSignifier chips the label
// dropdown draws, on ONE line, with a "+N" chip when they do not fit and a hover box listing them all
// (spec 010 D21 — the ticket's "+N", not the canvas's wrapping; see open item OQ-1).
import React, {useLayoutEffect, useRef, useState} from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import Styled from 'styled-components';
import {Tooltip, WorkSignifier} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

export interface LabelsColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// px: the default width (and what a grip double-click resets to) and the drag lower bound.
	size?: number;
	minSize?: number;
	// Wraps the rendered chips (the call site supplies the EditableCell).
	cellContent: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
	// Muted hover hint in an empty cell, shown only when canEdit says the viewer can add one.
	addLabel: string;
	canEdit?: (row: OpportunityTableRow) => boolean;
}

const CHIP_GAP_PX = 4;
const OVERFLOW_CHIP_PX = 36; // room reserved for "+N"

/**
 * How many chips of the given widths fit on one line of `available` px, leaving room for the "+N" chip
 * unless every chip fits. Always at least one while there are any, so a long label never renders as
 * only "+1".
 */
export const fitCount = (widths: number[], available: number, overflowPx: number, gap: number): number => {
	if (!widths.length) return 0;
	const total = widths.reduce((sum, w, i) => sum + w + (i ? gap : 0), 0);
	if (total <= available) return widths.length;
	let used = 0;
	let count = 0;
	for (const [i, w] of widths.entries()) {
		const next = used + w + (i ? gap : 0);
		if (next + gap + overflowPx > available) break;
		used = next;
		count++;
	}
	return Math.max(1, count);
};

const TooltipHost = Styled.span`
	display: block;
	min-width: 0;
	width: 100%;
`;
const Line = Styled.div`
	display: flex;
	align-items: center;
	flex-wrap: nowrap;
	gap: ${CHIP_GAP_PX}px;
	min-width: 0;
	width: 100%;
	overflow: hidden;
`;
const Chip = Styled.span`
	flex: none;
	/* With "+N" showing, a chip too long for what is left gives way with an ellipsis, so "+N" stays in the cell.
	   Every other time chips keep their natural width, which is also what LabelChips measures. */
	[data-overflowing] > & {
		flex: 0 1 auto;
		min-width: 0;
	}
	[data-overflowing] > & > div {
		max-width: 100%;
	}
	[data-overflowing] > & > div > div {
		display: block;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;
const More = Styled.span`
	flex: none;
	padding: 0 6px;
	border-radius: 4px;
	background: #f4f4f5;
	color: #52525b;
	font-size: 12px;
	line-height: 24px;
`;
// Shown only inside the editable trigger button (EditableCell's Trigger), on hover or keyboard focus.
const Hint = Styled.span`
	color: #a1a1aa;
	opacity: 0;
	button:hover > &,
	button:focus-visible > & {
		opacity: 1;
	}
`;
// The hover box shows every label in full, so a label wider than the box wraps inside its chip instead of
// spilling out of it: the design-system chip never wraps its text on its own.
const TooltipList = Styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${CHIP_GAP_PX}px;
	max-width: 100%;
	& > div {
		max-width: 100%;
	}
	& > div > div {
		min-width: 0;
		white-space: normal;
		overflow-wrap: anywhere;
	}
`;

// Measures after paint and again whenever the cell's width changes (D7: columns resize at runtime).
const LabelChips = ({labels}: {labels: OpportunityTableRow['labels']}): JSX.Element => {
	const lineRef = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(labels.length);

	useLayoutEffect(() => {
		const line = lineRef.current;
		if (!line) return undefined;
		const measure = () => {
			// Hidden chips are display:none and report no width, so reveal them all, measure, then settle.
			// Shrinking is held off too, so each chip reports its full width rather than a squeezed one.
			const chips = Array.from(line.querySelectorAll<HTMLElement>('[data-label-chip]'));
			chips.forEach(c => {
				c.style.display = '';
				c.style.flexShrink = '0';
			});
			const fits = fitCount(
				chips.map(c => c.offsetWidth),
				line.clientWidth,
				OVERFLOW_CHIP_PX,
				CHIP_GAP_PX
			);
			// Put the overflow back to hidden ourselves: when the count is unchanged React skips the render,
			// and would otherwise leave the chips revealed above on screen.
			chips.forEach((c, i) => {
				c.style.display = i < fits ? '' : 'none';
				c.style.flexShrink = '';
			});
			setVisible(fits);
		};
		measure();
		if (typeof ResizeObserver === 'undefined') return undefined;
		const observer = new ResizeObserver(measure);
		observer.observe(line);
		return () => observer.disconnect();
	}, [labels]);

	const hidden = labels.length - visible;
	return (
		<Tooltip
			content={
				<TooltipList>
					{labels.map(l => (
						<WorkSignifier key={l.id} workId={l.name} color={l.color} />
					))}
				</TooltipList>
			}
		>
			<TooltipHost>
				<Line ref={lineRef} data-cy="labels-cell" data-overflowing={hidden > 0 || undefined}>
					{labels.map((l, i) => (
						<Chip key={l.id} data-label-chip style={i < visible ? undefined : {display: 'none'}}>
							<WorkSignifier workId={l.name} color={l.color} />
						</Chip>
					))}
					{hidden > 0 && <More data-cy="labels-more">+{hidden}</More>}
				</Line>
			</TooltipHost>
		</Tooltip>
	);
};

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LabelsColumn = (_: LabelsColumnType): null => null;

LabelsColumn.toColumnDef = (child: React.ReactElement<LabelsColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {labels} = row.original;
		const content = labels.length ? (
			<LabelChips labels={labels} />
		) : child.props.canEdit?.(row.original) ? (
			<Hint>{child.props.addLabel}</Hint>
		) : null;
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-labels-cell">
				{child.props.cellContent(content, row.original)}
			</StyledCell>
		);
	},
});

export default LabelsColumn;
